import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { AgentAvatar } from "@/components/AgentAvatar";
import { VerifiedBadge, CategoryBadge, StatusPill, CapabilityBadge } from "@/components/AgentBadge";
import { getCategory } from "@/lib/categories";
import { cn } from "@/lib/utils";

const fmt = (n) => (n >= 1000 ? `${(n / 1000).toFixed(1)}K` : `${n}`);

export const AgentCard = ({ agent, className = "" }) => {
  const c = getCategory(agent.category);
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.4 }}
      className={cn("glass glass-hover group relative overflow-hidden rounded-2xl p-5", className)}
      data-testid={`agent-card-${agent.username}`}
    >
      <div
        className="pointer-events-none absolute -right-10 -top-10 h-28 w-28 rounded-full opacity-30 blur-2xl transition-opacity group-hover:opacity-60"
        style={{ background: c.hex }}
      />
      <div className="flex items-start gap-3">
        <AgentAvatar color={c.hex} category={agent.category} size={52} status={agent.status} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="truncate font-heading text-base font-bold text-white">{agent.name}</span>
            {agent.verified && <VerifiedBadge />}
          </div>
          <div className="truncate font-mono text-xs text-slate-400">@{agent.username}</div>
        </div>
        <StatusPill status={agent.status} />
      </div>

      <p className="mt-3 line-clamp-2 text-sm text-slate-300">{agent.description}</p>

      <div className="mt-3 flex flex-wrap gap-1.5">
        <CategoryBadge category={agent.category} />
        {(agent.capabilities || []).slice(0, 3).map((cap) => (
          <CapabilityBadge key={cap} label={cap} />
        ))}
      </div>

      <div className="mt-4 flex items-center justify-between border-t border-white/5 pt-3">
        <div className="flex items-center gap-4 text-xs text-slate-400">
          <span><span className="font-semibold text-white">{fmt(agent.followers)}</span> followers</span>
          <span className="flex items-center gap-1">
            <span className="font-mono font-semibold" style={{ color: c.hex }}>{Number(agent.reputation).toFixed(1)}</span> rep
          </span>
        </div>
        <Link
          to={`/agent/${agent.username}`}
          data-testid={`view-profile-${agent.username}`}
          className="rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:border-cyan-400/40 hover:text-cyan-200"
        >
          View Profile
        </Link>
      </div>
    </motion.div>
  );
};

export default AgentCard;
